import React, { useState, useCallback } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import {
    Box, Typography, IconButton, Chip, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, InputAdornment, Card, CardContent, Stack, Avatar, Divider,
    Menu, MenuItem, ListItemIcon, ListItemText, Grid, CircularProgress, Tooltip,
    InputLabel, FormControl, OutlinedInput, Switch, FormControlLabel, FormHelperText
} from '@mui/material';
import {
    Edit, DeleteOutline, CheckCircle, ErrorOutline, Refresh, Star as StarIcon, MoreVert as MoreVertIcon,
    Visibility as ViewIcon, VisibilityOff, Key as KeyIcon, Send as SendIcon,
    Link as LinkIcon, Add as AddIcon, Search
} from '@mui/icons-material';
import { DynamicIcon } from '../components/icons.jsx';
import { useTheme } from '@mui/material/styles';
import { LoadingSpinner, ConfirmModal } from '../components/Common.jsx';
import { SERVICE_CATALOG as STATIC_CATALOG, getServiceMeta as getStaticMeta } from '../config/serviceCatalog.js';
import { notify } from '@/components/core';

// Merge static + API catalog and find service meta
const getServiceMeta = (serviceId, catalog) => {
    if (catalog?.length) {
        const found = catalog.find(s => (s.service_id || s.id) === serviceId);
        if (found) return found;
    }
    return getStaticMeta(serviceId);
};


// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: API Key Card
// ─────────────────────────────────────────────────────────────────────────────
const ApiKeyCard = ({ keyItem, onEdit, onDelete, onTest, onSetDefault, readOnly, theme, catalog }) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [testing, setTesting] = useState(false);
    const meta = getServiceMeta(keyItem.service, catalog);
    const isPlatformKey = keyItem._isPlatformKey;

    const handleTest = async () => {
        setTesting(true);
        await onTest(keyItem);
        setTesting(false);
    };

    return (
        <Card elevation={0} sx={{
            border: '1px solid',
            borderColor: keyItem.is_default ? 'primary.main' : isPlatformKey ? 'warning.light' : 'divider',
            borderRadius: 2,
            transition: 'all 0.2s',
            opacity: readOnly ? 0.85 : 1,
            '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4], borderColor: isPlatformKey ? 'warning.main' : 'primary.main' }
        }}>
            <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar variant="rounded" sx={{ bgcolor: `${meta.color}18`, width: 48, height: 48 }}>
                        <DynamicIcon name={meta.icon} className="h-6 w-6" style={{ color: meta.color }} />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="subtitle1" fontWeight="bold" noWrap>{keyItem.label}</Typography>
                            {keyItem.is_default && (
                                <Chip label="Default" size="small" color="primary" icon={<StarIcon sx={{ fontSize: '12px !important' }} />}
                                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                            )}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <Chip label={meta.label} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${meta.color}18`, color: meta.color, fontWeight: 700 }} />
                            {isPlatformKey && (
                                <Chip label="Platform" size="small" color="warning" variant="outlined"
                                    sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }} />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                ••••••••
                            </Typography>
                        </Stack>
                    </Box>
                    {!readOnly && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                        icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
                        label={isPlatformKey ? 'Platform Default' : 'Active'}
                        size="small"
                        color={isPlatformKey ? 'warning' : 'success'}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                    {!readOnly && (
                        <Button
                            size="small"
                            startIcon={testing ? <CircularProgress size={12} /> : <SendIcon fontSize="small" />}
                            onClick={handleTest}
                            disabled={testing}
                            sx={{ fontSize: '0.75rem' }}
                        >
                            Test
                        </Button>
                    )}
                </Stack>
            </Box>

            {!readOnly && (
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                    {!keyItem.is_default && (
                        <MenuItem onClick={() => { setMenuAnchor(null); onSetDefault(keyItem); }}>
                            <ListItemIcon><StarIcon fontSize="small" color="primary" /></ListItemIcon>
                            <ListItemText>Set as Default</ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => { setMenuAnchor(null); onEdit(keyItem); }}>
                        <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { setMenuAnchor(null); onDelete(keyItem); }} sx={{ color: 'error.main' }}>
                        <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </Menu>
            )}
        </Card>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Dynamic Form Field
// ─────────────────────────────────────────────────────────────────────────────
const DynamicField = ({ field, value, onChange, isEditing }) => {
    const [showPwd, setShowPwd] = useState(false);

    if (field.type === 'password') {
        return (
            <FormControl fullWidth size="small">
                <InputLabel>{field.label}{field.required && ' *'}</InputLabel>
                <OutlinedInput
                    type={showPwd ? 'text' : 'password'}
                    value={value || ''}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    label={`${field.label}${field.required ? ' *' : ''} `}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPwd(p => !p)} edge="end">
                                {showPwd ? <VisibilityOff fontSize="small" /> : <ViewIcon fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    }
                    placeholder={field.required ? 'Required' : 'Optional'}
                />
                {isEditing && (
                    <FormHelperText>Leave as ******** to keep existing secret unchanged.</FormHelperText>
                )}
            </FormControl>
        );
    }

    return (
        <TextField
            label={`${field.label}${field.required ? ' *' : ''} `}
            size="small"
            fullWidth
            type={field.type || 'text'}
            value={value || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder={field.required ? 'Required' : 'Optional'}
        />
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: API Key Form Dialog
// ─────────────────────────────────────────────────────────────────────────────
const ApiKeyFormDialog = ({ open, onClose, onSave, editingKey, catalog }) => {
    const [step, setStep] = useState(editingKey ? 'form' : 'pick');
    const [selected, setSelected] = useState(
        editingKey ? getServiceMeta(editingKey.service, catalog) : null
    );
    const [label, setLabel] = useState(editingKey?.label || '');
    const [isDefault, setIsDefault] = useState(editingKey ? !!editingKey.is_default : false);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);

    // Effective catalog: API data (which merges built-ins + custom) or fall back to static
    const effectiveCatalog = catalog?.length ? catalog : STATIC_CATALOG;

    React.useEffect(() => {
        if (open) {
            setStep(editingKey ? 'form' : 'pick');
            setSelected(editingKey ? getServiceMeta(editingKey.service, catalog) : null);
            setLabel(editingKey?.label || '');
            setIsDefault(editingKey ? !!editingKey.is_default : false);
            setValues(editingKey?.key_values || {});
            setSaving(false);
        }
    }, [open, editingKey, catalog]);

    const handleClose = () => {
        setStep(editingKey ? 'form' : 'pick');
        setSelected(editingKey ? getServiceMeta(editingKey.service, catalog) : null);
        setLabel(editingKey?.label || '');
        setIsDefault(editingKey ? !!editingKey.is_default : false);
        setValues({});
        onClose();
    };

    const handlePick = (svc) => {
        setSelected(svc);
        setLabel(editingKey?.label || `My ${svc.label} `);
        setIsDefault(false);
        setStep('form');
    };

    const handleFieldChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        if (!label.trim()) return;
        setSaving(true);
        // service_id for API catalog entries, id for static ones
        const serviceKey = selected.service_id || selected.id;
        await onSave({ service: serviceKey, label, is_default: isDefault, key_values: values });
        setSaving(false);
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {step === 'pick' ? 'Select a Service' : (editingKey ? `Edit ${selected?.label} Key` : `Add ${selected?.label} Key`)}
            </DialogTitle>

            <DialogContent>
                {step === 'pick' ? (
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        {effectiveCatalog.map(svc => (
                            <Grid item xs={6} key={svc.service_id || svc.id}>
                                <Card
                                    onClick={() => handlePick(svc)}
                                    sx={{ cursor: 'pointer', border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                                >
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '12px !important' }}>
                                        <Avatar variant="rounded" sx={{ bgcolor: `${svc.color} 18`, width: 36, height: 36 }}>
                                            <DynamicIcon name={svc.icon} className="h-5 w-5" style={{ color: svc.color }} />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">{svc.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{svc.description}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {!editingKey && (
                            <Button size="small" onClick={() => setStep('pick')} sx={{ alignSelf: 'flex-start' }}>
                                ← Back
                            </Button>
                        )}
                        <TextField
                            label="Label *"
                            size="small"
                            fullWidth
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder={`e.g.My ${selected?.label} Key`}
                            helperText="A friendly name to identify this key"
                        />
                        {selected?.fields.map(f => (
                            <DynamicField key={f.key} field={f} value={values[f.key]} onChange={handleFieldChange} isEditing={!!editingKey} />
                        ))}
                        <FormControlLabel
                            control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} color="primary" />}
                            label={<Typography variant="body2">Set as default service for this category</Typography>}
                        />
                    </Stack>
                )}
            </DialogContent>

            {step === 'form' && (
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !label.trim()}
                        startIcon={saving ? <CircularProgress size={14} /> : null}
                    >
                        {editingKey ? 'Update Key' : 'Save Key'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const IntegrationsSettings = ({ tenantId, token }) => {
    const theme = useTheme();
    const { data: ownKeys, isLoading, refresh } = useData(
        tenantId ? `/integrations/tenants/${tenantId}/api-keys` : null, token
    );
    const { data: platformKeys } = useData('/integrations/platform/api-keys', token);
    const { data: catalog } = useData('/integrations/platform/service-types', token);

    const [formOpen, setFormOpen] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Merge own keys + platform keys (platform flagged as read-only)
    const allKeys = [
        ...(ownKeys || []),
        ...(platformKeys || []).map(k => ({ ...k, _isPlatformKey: true })),
    ];

    const filteredKeys = allKeys.filter(k =>
        k.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        k.service?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSave = async ({ service, label, is_default, key_values }) => {
        try {
            if (editingKey) {
                const payload = { label, is_default };
                const filledValues = Object.fromEntries(Object.entries(key_values).filter(([, v]) => v !== undefined && v !== '********'));
                payload.key_values = filledValues;
                await apiFetch(`/integrations/tenants/${tenantId}/api-keys/${editingKey.id}`, token, { method: 'PUT', body: JSON.stringify(payload) });
                notify.success('API key updated.');
            } else {
                await apiFetch(`/integrations/tenants/${tenantId}/api-keys`, token, { method: 'POST', body: JSON.stringify({ service, label, is_default, key_values }) });
                notify.success('API key saved.');
            }
            refresh();
        } catch (e) {
            notify.error(e.message || 'Failed to save key.');
        }
    };

    const handleSetDefault = async (keyItem) => {
        try {
            await apiFetch(`/integrations/tenants/${tenantId}/api-keys/${keyItem.id}`, token, {
                method: 'PUT',
                body: JSON.stringify({ label: keyItem.label, is_default: true, key_values: {} }),
            });
            notify.error(`"${keyItem.label}" set as default.`);
            refresh();
        } catch (e) {
            notify.error(e.message || 'Failed to set as default.');
        }
    };

    const handleTest = async (keyItem) => {
        try {
            const res = await apiFetch(`/integrations/tenants/${tenantId}/api-keys/${keyItem.id}/test`, token, { method: 'POST', body: JSON.stringify({ recipient_email: null }) });
            notify.success(res.message || 'Test successful.');
        } catch (e) {
            notify.error(e.message || 'Test failed.');
        }
    };

    const confirmDeleteAction = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/integrations/tenants/${tenantId}/api-keys/${confirmDelete.id}`, token, { method: 'DELETE' });
            notify.success('API key deleted.');
            refresh();
        } catch (e) {
            notify.error(e.message || 'Failed to delete.');
        } finally {
            setConfirmDelete(null);
        }
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
            {/* Page Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">Integrations</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your keys and connections to external services.
                    </Typography>
                </Box>
            </Stack>

            {/* ── Section 1: API Keys & Services ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <KeyIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">API Keys &amp; Services</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Store your own API keys for SendGrid, SMTP, Twilio, OpenAI, etc.
                        If none configured, the platform's shared keys will be used automatically.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingKey(null); setFormOpen(true); }}>
                    Add Key
                </Button>
            </Stack>

            <TextField
                placeholder="Search keys..."
                size="small"
                sx={{ mb: 2, width: 280 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />

            {isLoading ? <LoadingSpinner /> : filteredKeys.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider', mb: 4 }}>
                    <KeyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No API keys configured.</Typography>
                    <Typography variant="caption" color="text.secondary">
                        The platform's default keys are being used. Add your own to override them.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Button onClick={() => { setEditingKey(null); setFormOpen(true); }}>Add Your First Key</Button>
                    </Box>
                </Box>
            ) : (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {filteredKeys.map(k => (
                        <Grid item xs={12} sm={6} md={4} key={k.id}>
                            <ApiKeyCard
                                keyItem={k}
                                theme={theme}
                                catalog={catalog}
                                readOnly={!!k._isPlatformKey}
                                onEdit={(item) => { setEditingKey(item); setFormOpen(true); }}
                                onDelete={setConfirmDelete}
                                onTest={handleTest}
                                onSetDefault={handleSetDefault}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}


            {/* API Key Form Dialog */}
            <ApiKeyFormDialog
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditingKey(null); }}
                onSave={handleSave}
                editingKey={editingKey}
                catalog={catalog}
            />

            {/* Confirm Delete */}
            <ConfirmModal
                isOpen={Boolean(confirmDelete)}
                onClose={() => setConfirmDelete(null)}
                onConfirm={confirmDeleteAction}
                title={`Delete "${confirmDelete?.label}"?`}
                message="This API key will be permanently deleted. Features that rely on it will fall back to the platform's shared keys."
            />
        </Box>
    );
};

export default IntegrationsSettings;
